import { useEffect, useRef, useState } from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Tooltip, Typography, styled } from '@mui/material';

import { SentryMuiWrapper } from './minicomponents';

const InfoIcon = styled(InfoOutlinedIcon)({
  width: 24,
  height: 24,
  color: 'secondary.light',
});

export function InfoTooltipView({
  text,
  onTooltipOpenCallback,
  icon = <InfoIcon />,
  forceOpen,
  isDisabled,
  onClick,
  autoCloseDelay,
  isEventPropagationEnabled,
}: {
  text?: React.ReactElement | string | false | React.ReactNode;
  onTooltipOpenCallback?: () => void;
  icon?: React.ReactElement;
  forceOpen?: boolean;
  isDisabled?: boolean;
  onClick?: () => void;
  autoCloseDelay?: number;
  isEventPropagationEnabled?: boolean;
}) {
  const [isOpen, setOpen] = useState<boolean>(!!forceOpen);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isDisabled) {
      setOpen(!!forceOpen || isHovered);
    }
  }, [forceOpen, isDisabled, isHovered]);

  useEffect(() => {
    if (isOpen && autoCloseDelay) {
      timerRef.current = setTimeout(() => {
        setOpen(false);
      }, autoCloseDelay);
    }
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [isOpen, autoCloseDelay]);

  function handleTooltipClose() {
    if (!isDisabled && !forceOpen) {
      setOpen(false);
      setIsHovered(false);
    }
  }

  function handleTooltipToggle(event: React.MouseEvent) {
    if (!isEventPropagationEnabled) {
      event.stopPropagation(); // enables including hover tooltips inside clickable elements
    }
    if (isDisabled) return;
    if (!forceOpen) {
      setIsHovered(!isHovered);
    }
    if (onClick) onClick();
    if (!isOpen && onTooltipOpenCallback) onTooltipOpenCallback();
  }

  function handleMouseEnter() {
    if (!isDisabled && !forceOpen) {
      setIsHovered(true);
    }
  }

  function handleMouseLeave() {
    if (!isDisabled && !forceOpen) {
      setIsHovered(false);
    }
  }

  const displayText = text ? (
    <Typography variant="caption" color="primary.contrastText" textAlign={'center'}>
      {text}
    </Typography>
  ) : null;

  const content = (
    <Box
      onClick={handleTooltipToggle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span>{icon}</span>
    </Box>
  );

  if (!text) {
    return content;
  }

  return (
    <SentryMuiWrapper onClickAway={handleTooltipClose}>
      <Tooltip
        title={displayText}
        open={isDisabled ? false : isOpen}
        arrow
        placement="top"
        disableFocusListener
        disableHoverListener={isDisabled || forceOpen}
        disableTouchListener={isDisabled || forceOpen}
      >
        {content}
      </Tooltip>
    </SentryMuiWrapper>
  );
}
